<template>
  <a-card
    size="small"
    :body-style="{ padding: '0' }"
    class="ws-table-card"
  >
    <div class="ws-table-toolbar">
      <a-input-search
        v-model:value="searchTerm"
        placeholder="Search device or task"
        style="max-width: 260px"
        allow-clear
      />
      <a-select
        v-model:value="modeFilter"
        placeholder="All modes"
        allow-clear
        style="min-width: 160px"
        :options="modeFilterOptions"
      />
    </div>

    <a-table
      :columns="visibleColumns"
      :data-source="rows"
      :pagination="{ pageSize: 15, position: ['bottomCenter'] }"
      row-key="serial"
      size="middle"
      :scroll="{ x: 'max-content' }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.dataIndex === 'device'">
          <div class="ws-device-cell">
            <span class="ws-device-name" @click="handleOpen(record)">
              {{ record.deviceName }}
            </span>
            <star-filled v-if="record.starred" class="ws-star" />
          </div>
          <div class="ws-device-serial">{{ record.serial }}</div>
        </template>

        <template v-if="column.dataIndex === 'mode'">
          <a-tag :color="modeTagColor(record.mode)">
            <span class="dot" :style="{ background: modeDotColor(record.mode) }" />
            {{ modeLabel(record.mode) }}
          </a-tag>
        </template>

        <template v-if="column.dataIndex === 'task'">
          <span v-if="record.taskName" class="ws-task-name">
            {{ record.taskName }}
          </span>
          <span v-else class="ws-muted">—</span>
        </template>

        <template v-if="column.dataIndex === 'cycles'">
          <span v-if="record.cyclesToday != null" class="ws-strong">
            {{ record.cyclesToday.toLocaleString() }}
          </span>
          <span v-else class="ws-muted">—</span>
        </template>

        <template v-if="column.dataIndex === 'badPct'">
          <span v-if="record.badPct != null">
            {{ record.badPct.toFixed(1) }}%
          </span>
          <span v-else class="ws-muted">—</span>
        </template>

        <template v-if="column.dataIndex === 'topMissed'">
          <div v-if="record.topMissed.length" class="ws-missed-list">
            <a-tooltip
              v-for="(step, i) in record.topMissed"
              :key="i"
              :title="step.step_name"
            >
              <span class="ws-missed-tag">
                <span class="ws-missed-rank">{{ i + 1 }}</span>
                <span class="ws-missed-name">{{ step.step_name }}</span>
                <span class="ws-missed-count">{{ step.count }}</span>
              </span>
            </a-tooltip>
          </div>
          <span v-else class="ws-muted">—</span>
        </template>

        <template v-if="column.dataIndex === 'costAvoided'">
          <span v-if="record.costAvoided > 0" class="ws-cost">
            {{ formatMoney(record.costAvoided) }}
          </span>
          <span
            v-else-if="record.mode === 'guidance' && !record.hasConfig"
            class="ws-config-link"
            @click="handleConfigure(record)"
          >
            Configure →
          </span>
          <span v-else class="ws-muted">—</span>
        </template>

        <template v-if="column.dataIndex === 'actions'">
          <a-space size="small">
            <a-tooltip title="Open Analytics">
              <a-button
                type="text"
                size="small"
                :disabled="!record.isActive"
                @click="handleOpen(record)"
              >
                <template #icon><line-chart-outlined /></template>
              </a-button>
            </a-tooltip>
            <a-tooltip title="Open Trace">
              <a-button
                type="text"
                size="small"
                :disabled="!record.isActive"
                @click="handleOpenTrace(record)"
              >
                <template #icon><play-circle-outlined /></template>
              </a-button>
            </a-tooltip>
          </a-space>
        </template>
      </template>
    </a-table>
  </a-card>
</template>

<script>
import {
  LineChartOutlined,
  PlayCircleOutlined,
  StarFilled,
} from '@ant-design/icons-vue';
import { roughCostAvoided } from 'src/utils/valueStory/enrichSteps';
import { mapGetters } from 'vuex';

const MODE_LABELS = {
  guidance: 'Guidance',
  recording: 'Recording',
  idle: 'Idle',
  offline: 'Offline',
};
const MODE_TAG_COLORS = {
  guidance: 'success',
  recording: 'purple',
  idle: 'warning',
  offline: 'default',
};
const MODE_DOT_COLORS = {
  guidance: '#52c41a',
  recording: '#722ed1',
  idle: '#faad14',
  offline: '#bfbfbf',
};

export default {
  components: { LineChartOutlined, PlayCircleOutlined, StarFilled },
  props: {
    devices: { type: Array, required: true, default: () => [] },
    activeDevices: { type: Object, required: true, default: () => ({}) },
    activeDevicesStatus: { type: Object, required: true, default: () => ({}) },
    idToTaskNameMapping: { type: Object, required: true, default: () => ({}) },
    valueStoryConfigMap: { type: Object, required: false, default: () => ({}) },
  },
  data() {
    return {
      searchTerm: '',
      modeFilter: null,
      columns: [
        { title: 'Device', dataIndex: 'device', width: 220 },
        {
          title: 'Mode',
          dataIndex: 'mode',
          width: 130,
          filters: [
            { text: 'Guidance', value: 'guidance' },
            { text: 'Recording', value: 'recording' },
            { text: 'Idle', value: 'idle' },
            { text: 'Offline', value: 'offline' },
          ],
          onFilter: (value, record) => record.mode === value,
        },
        { title: 'Operation', dataIndex: 'task', ellipsis: true },
        {
          title: 'Cycles today',
          dataIndex: 'cycles',
          width: 130,
          sorter: (a, b) => (a.cyclesToday || 0) - (b.cyclesToday || 0),
        },
        {
          title: 'Bad rate',
          dataIndex: 'badPct',
          width: 110,
          sorter: (a, b) => (a.badPct || 0) - (b.badPct || 0),
        },
        { title: 'Top missed steps', dataIndex: 'topMissed', width: 320 },
        {
          title: 'Cost avoided',
          dataIndex: 'costAvoided',
          width: 160,
          sorter: (a, b) => (a.costAvoided || 0) - (b.costAvoided || 0),
        },
        { title: 'Actions', dataIndex: 'actions', width: 110 },
      ],
      modeFilterOptions: [
        { label: 'Guidance', value: 'guidance' },
        { label: 'Recording', value: 'recording' },
        { label: 'Idle', value: 'idle' },
        { label: 'Offline', value: 'offline' },
      ],
    };
  },
  computed: {
    ...mapGetters(['hasValueStoryAccess']),
    visibleColumns() {
      if (this.hasValueStoryAccess) return this.columns;
      return this.columns.filter((c) => c.dataIndex !== 'costAvoided');
    },
    rows() {
      const q = this.searchTerm.trim().toLowerCase();
      return this.devices
        .map((d) => this.buildRow(d))
        .filter((r) => {
          if (this.modeFilter && r.mode !== this.modeFilter) return false;
          if (!q) return true;
          return (
            r.deviceName.toLowerCase().includes(q) ||
            (r.taskName || '').toLowerCase().includes(q) ||
            r.serial.toLowerCase().includes(q)
          );
        });
    },
  },
  methods: {
    buildRow(device) {
      const settings = this.activeDevices[device.Serial_number];
      const active = Boolean(settings?.active);
      const guidance = Boolean(settings?.isInferenceRunning);
      const recording = Boolean(settings?.record_shift);
      let mode = 'offline';
      if (active) mode = recording ? 'recording' : guidance ? 'guidance' : 'idle';
      const taskId = settings?.Task;
      const stats = this.activeDevicesStatus[`${device.Serial_number}${taskId}`] || {};
      const cycles = Number(stats.number_of_cycles);
      const bad = Number(stats.bad_cycles);
      const cfg = this.valueStoryConfigMap?.[taskId];
      const hasConfig = !!(
        cfg &&
        ((cfg.steps && Object.keys(cfg.steps).length) ||
          (cfg.economics && Object.keys(cfg.economics).length))
      );
      const costAvoided = hasConfig && mode === 'guidance' && bad
        ? roughCostAvoided(cfg, bad)
        : 0;
      return {
        serial: device.Serial_number,
        deviceName: device.display_name || device.Serial_number,
        starred: !!device.starred,
        isActive: active,
        mode,
        taskId,
        taskName: this.idToTaskNameMapping[taskId] || '',
        cyclesToday: Number.isFinite(cycles) ? cycles : null,
        badPct:
          Number.isFinite(cycles) && cycles > 0 && Number.isFinite(bad)
            ? (bad / cycles) * 100
            : null,
        hasConfig,
        costAvoided,
        topMissed: Array.isArray(stats.top_missed_steps)
          ? stats.top_missed_steps.slice(0, 3)
          : [],
        raw: device,
      };
    },
    modeLabel(m) {
      return MODE_LABELS[m];
    },
    modeTagColor(m) {
      return MODE_TAG_COLORS[m];
    },
    modeDotColor(m) {
      return MODE_DOT_COLORS[m];
    },
    formatMoney(v) {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return '$0';
      if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
      return `$${Math.round(n).toLocaleString()}`;
    },
    handleOpen(row) {
      if (!row.isActive || !row.taskId) return;
      const { href } = this.$router.resolve({
        name: 'Operation Level Analytics',
        query: {
          taskId: row.taskId,
          deviceId: JSON.stringify([row.serial]),
        },
      });
      window.open(href, '_blank');
    },
    handleOpenTrace(row) {
      if (!row.isActive || !row.taskId) return;
      const { href } = this.$router.resolve({
        name: 'Trace',
        query: {
          taskId: row.taskId,
          deviceId: JSON.stringify([row.serial]),
        },
      });
      window.open(href, '_blank');
    },
    handleConfigure(row) {
      if (!row.taskId) return;
      const { href } = this.$router.resolve({
        name: 'Define Operation',
        query: { openValueStory: row.taskId },
      });
      window.open(href, '_blank');
    },
  },
};
</script>

<style scoped>
.ws-table-card {
  border-radius: 12px;
  overflow: hidden;
}
.ws-table-toolbar {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}
.ws-device-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ws-device-name {
  color: #1890ff;
  font-weight: 600;
  cursor: pointer;
}
.ws-device-name:hover {
  text-decoration: underline;
}
.ws-device-serial {
  color: #bfbfbf;
  font-size: 11px;
  margin-top: 2px;
}
.ws-star {
  color: #faad14;
  font-size: 12px;
}
.ws-strong {
  font-weight: 600;
}
.ws-muted {
  color: #bfbfbf;
}
.ws-task-name {
  color: #595959;
}
.ws-cost {
  color: #237804;
  font-weight: 700;
}
.ws-config-link {
  color: #8c8c8c;
  font-size: 12px;
  cursor: pointer;
}
.ws-config-link:hover {
  color: #1890ff;
}
.dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  margin-right: 6px;
  vertical-align: middle;
}
.ws-missed-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ws-missed-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 6px;
  border-radius: 6px;
  background: #fff2e8;
  color: #ad4e00;
  font-size: 11px;
  max-width: 300px;
}
.ws-missed-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  background: #ffbb96;
  color: #7c2d00;
  font-weight: 700;
  font-size: 10px;
  flex-shrink: 0;
}
.ws-missed-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #595959;
}
.ws-missed-count {
  font-weight: 700;
  color: #ad4e00;
  margin-left: auto;
}
</style>
